import { useEffect, useMemo, useState } from 'react';
import { Label } from 'react-aria-components';
import { Control, FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form';

import DateField from '@/components/DateField/DateField';
import RadioGroup from '@/components/RadioGroup/RadioGroup';
import SelectField from '@/components/SelectField/SelectField';
import TextAreaInput from '@/components/TextArea/TextAreaInput';
import TimeField from '@/components/TimeField/TimeField';
import { formattedDate } from '@/logics/date/date';
import { SupportRecordFormData } from '@/pages/admin/reservations/support-records/[id]';
import { DetailedAttendanceReservation } from '@/types/attendanceReservation';
import { SupportRecord } from '@/types/supportRecord';

type Props = {
  reservation: DetailedAttendanceReservation | undefined;
  supportRecord: SupportRecord | undefined;
  control: Control<SupportRecordFormData>;
  register: UseFormRegister<SupportRecordFormData>;
  watch: UseFormWatch<SupportRecordFormData>;
  errors: FieldErrors<SupportRecordFormData>;
};

export const UsageStatusForm = ({
  reservation,
  supportRecord,
  control,
  register,
  watch,
  errors,
}: Props) => {
  const [isRescheduled, setIsRescheduled] = useState(false);
  const targetDate = useMemo(() => {
    if (supportRecord) {
      return supportRecord.date;
    } else if (reservation) {
      return reservation.date;
    }
    return '';
  }, [supportRecord, reservation]);

  useEffect(() => {
    /* @ts-expect-error 理想はbooleanで対応するべきだが一旦文字列でtrue false入れている */
    if (watch('isRescheduled') === 'true') {
      setIsRescheduled(true);
    } else {
      setIsRescheduled(false);
    }
  }, [watch('isRescheduled')]);

  return (
    <div className="space-y-4">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="border border-gray-300 bg-gray-100 p-2 text-center">
              サービス利用開始日
            </th>
            <th className="border border-gray-300 bg-gray-100 p-2 text-center">提供時間開始</th>
            <th className="border border-gray-300 bg-gray-100 p-2 text-center">提供時間終了</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 p-2 text-center">{targetDate}</td>
            <td className="border border-gray-300 p-2">
              <TimeField
                label=""
                required
                name="startTime"
                control={control}
                errors={errors.startTime}
              />
            </td>
            <td className="border border-gray-300 p-2">
              <TimeField
                label=""
                required
                name="endTime"
                control={control}
                errors={errors.endTime}
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div className="space-y-4">
        <div className="w-fit">
          <SelectField
            name="attendanceStatus"
            label="出欠情報"
            control={control}
            items={[
              { value: 'attend', label: '出席' },
              { value: 'absent', label: '欠席' },
            ]}
          />
        </div>

        {watch('attendanceStatus') === 'attend' ? (
          <>
            <div className="w-fit">
              <TimeField
                label="送迎（来所）"
                required
                name="pickupOrArrivalTime"
                control={control}
                errors={errors.pickupOrArrivalTime}
              />
            </div>
            <div className="w-fit">
              <RadioGroup
                title="送迎加算（来所）"
                required={true}
                errors={errors.isPickupAddition}
                options={[
                  {
                    id: 'true',
                    label: 'あり',
                    value: 'true',
                  },
                  {
                    id: 'false',
                    label: 'なし',
                    value: 'false',
                  },
                ]}
                initialChecked={supportRecord?.is_pickup_addition ? 'true' : 'false'}
                {...register('isPickupAddition')}
              />
            </div>
            <div className="w-fit">
              <TimeField
                label="送迎（退所）"
                required
                name="dropoffOrDepartureTime"
                control={control}
                errors={errors.dropoffOrDepartureTime}
              />
            </div>
            <div className="w-fit">
              <RadioGroup
                title="送迎加算（退所）"
                required={true}
                errors={errors.isDropoffAddition}
                options={[
                  {
                    id: 'true',
                    label: 'あり',
                    value: 'true',
                  },
                  {
                    id: 'false',
                    label: 'なし',
                    value: 'false',
                  },
                ]}
                initialChecked={supportRecord?.is_dropoff_addition ? 'true' : 'false'}
                {...register('isDropoffAddition')}
              />
            </div>
          </>
        ) : (
          <>
            <div className="w-full space-y-2">
              <Label className="mb-2 text-lg font-bold">欠席連絡日</Label>
              <div>
                {supportRecord
                  ? formattedDate(new Date(supportRecord.absent_date), 'yyyy年MM月dd日(E) HH:mm')
                  : reservation?.absent_date
                    ? formattedDate(new Date(reservation.absent_date), 'yyyy年MM月dd日(E) HH:mm')
                    : '来所予約時に欠席連絡日を入力できるようにする'}
              </div>
            </div>
            <div className="w-full space-y-2">
              <Label className="mb-2 text-lg font-bold">欠席理由・申し送り事項（保護者）</Label>
              <div>
                {supportRecord
                  ? supportRecord.absent_reason_by_guardian
                  : reservation?.absent_reason}
              </div>
            </div>
            <div className="w-full">
              <TextAreaInput
                label="欠席理由・申し送り事項（事業所）"
                required
                errors={errors.absentReasonByStaff}
                {...register('absentReasonByStaff')}
              />
            </div>
            <div className="w-fit">
              <RadioGroup
                title="振替"
                required={true}
                errors={errors.isRescheduled}
                options={[
                  {
                    id: 'true',
                    label: '予定を振り替える',
                    value: 'true',
                  },
                  {
                    id: 'false',
                    label: '予定を振り替えない',
                    value: 'false',
                  },
                ]}
                initialChecked={supportRecord?.is_rescheduled ? 'true' : 'false'}
                {...register('isRescheduled')}
              />
            </div>
            {isRescheduled && (
              <div className="w-fit">
                <DateField
                  label="振替日程"
                  required
                  control={control}
                  name="rescheduledDate"
                  errors={errors.rescheduledDate}
                />
              </div>
            )}
            <div className="w-fit">
              <RadioGroup
                title="欠席時加算"
                required={true}
                errors={errors.isAbsentAddition}
                options={[
                  {
                    id: 'true',
                    label: 'あり',
                    value: 'true',
                  },
                  {
                    id: 'false',
                    label: 'なし',
                    value: 'false',
                  },
                ]}
                initialChecked={supportRecord?.is_absent_addition ? 'true' : 'false'}
                {...register('isAbsentAddition')}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
